/* eslint-disable no-param-reassign */
import { cloneDeep, isEqual } from 'lodash';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import PushMessages from 'client/MFA/push/PushMessages.json';
import PushMfaApi from 'client/api/v2/PushMfaApi';
import Vue from 'vue';

export const INITIALIZE_STATE = 'INITIALIZE_STATE';
export const SET_CONFIG = 'SET_CONFIG';
export const SET_ERROR_INITIALIZING = 'SET_ERROR_INITIALIZING';
export const SET_ERROR_SAVING = 'SET_ERROR_SAVING';
export const SET_STARTING_STATE = 'SET_STARTING_STATE';
export const SET_STATE = 'SET_STATE';

const initialState = () => ({
  errorInitializing: false,
  errorSaving: false,
  config: {
    id: undefined,
    userVerification: undefined,
    concurrentLimit: null,
  },
});

export const createPushMfaConfigModule = api => ({
  namespaced: true,

  state: {
    ...initialState(),
    startingState: { ...initialState() },
  },

  actions: {
    changeConfig({ commit }, payload) {
      commit(SET_CONFIG, payload);
    },
    async fetchConfigs({ commit }) {
      const configs = await api.listConfigs();
      commit(SET_CONFIG, configs[0]);
    },
    async initialize({ commit, dispatch }) {
      commit(INITIALIZE_STATE);
      try {
        await dispatch('fetchConfigs');
        commit(SET_ERROR_INITIALIZING, false);
      } catch (exp) {
        commit(SET_ERROR_INITIALIZING, true);
        dispatch(`${notificationModule}/triggerErrorNotification`, {
          title: PushMessages.ConfigRequestError,
        }, { root: true });
        /* We should consider retrying here */
      }
      commit(SET_STARTING_STATE);
    },

    resetState({ commit }) {
      commit(SET_STATE);
    },
    /**
     * PUT's the changed config to the API
     */
    async saveConfigChange({ commit, state }) {
      const result = await api.putConfig({ ...state.config });
      commit(SET_CONFIG, result);
      commit(SET_STARTING_STATE);
    },
    /**
     * Re-initialize if we need to then dispatch the save action and handle notifications
     */
    async saveChanges({
      commit, state, dispatch, getters,
    }) {
      if (getters.hasChanges) {
        try {
          /**
           * If Push has never been enabled for the Org we will not have a config to save.
           * Because of this we need to first check if we have a valid config and try to
           * fetch one if we don't.
           */
          if (state.config.id == null) {
            await dispatch('fetchConfigs');
          }
          await dispatch('saveConfigChange');
          commit(SET_ERROR_SAVING, false);
          dispatch(`${notificationModule}/triggerSuccessNotification`, {
            title: PushMessages.ConfigSaved,
          }, { root: true });
        } catch {
          commit(SET_ERROR_SAVING, true);
          // Notify Error
          const errorMessage = PushMessages.SaveError;

          dispatch(`${notificationModule}/triggerErrorNotification`, {
            title: errorMessage,
          }, { root: true });
          throw new Error(errorMessage);
        }
      }
    },
  },

  getters: {
    hasChanges(state = {}) {
      return !isEqual(state.config, state.startingState.config);
    },
    hasErrors(state = {}) {
      return state.errorSaving || state.errorInitializing;
    },
  },

  mutations: {
    [INITIALIZE_STATE]: (state) => {
      const { config, errorSaving } = initialState();
      Vue.set(state, 'config', config);
      Vue.set(state, 'errorSaving', errorSaving);
      Vue.set(state, 'startingState', initialState());
    },
    [SET_CONFIG]: (state, config = {}) => {
      Vue.set(state.config, 'id', config.id);
      Vue.set(state.config, 'userVerification', config.userVerification);
      Vue.set(state.config, 'concurrentLimit', config.concurrentLimit);
    },
    [SET_ERROR_INITIALIZING]: (state, error = false) => {
      Vue.set(state, 'errorInitializing', error);
    },
    [SET_ERROR_SAVING]: (state, error = false) => {
      Vue.set(state, 'errorSaving', error);
    },
    [SET_STATE]: (state) => {
      Vue.set(state, 'config', cloneDeep(state.startingState.config));
      Vue.set(state, 'errorSaving', initialState().errorSaving);
    },
    [SET_STARTING_STATE]: (state) => {
      Vue.set(state.startingState, 'config', cloneDeep(state.config));
    },
  },
});

export default createPushMfaConfigModule(PushMfaApi);
